<template>
  <!-- @vue-expect-error function from inherited base component -->
  <div :class="cx('root')" v-bind="ptmi('root')">
    <!-- @vue-expect-error function from inherited base component -->
    <div ref="toolbarElement" :class="cx('toolbar')" v-bind="ptm('toolbar')">
      <slot name="toolbar">
        <span class="ql-formats">
          <select class="ql-header" defaultValue="0">
            <option value="0">Normal</option>
            <option value="1">Heading</option>
            <option value="2">Subheading</option>
          </select>
          <select class="ql-font">
            <option></option>
            <option value="serif"></option>
            <option value="monospace"></option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-bold" type="button"></button>
          <button class="ql-italic" type="button"></button>
          <button class="ql-underline" type="button"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-color"></select>
          <select class="ql-background"></select>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered" type="button"></button>
          <button class="ql-list" value="bullet" type="button"></button>
          <select class="ql-align">
            <option defaultValue></option>
            <option value="center"></option>
            <option value="right"></option>
            <option value="justify"></option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-link" type="button"></button>
          <button class="ql-image" type="button"></button>
          <button class="ql-code-block" type="button"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-clean" type="button"></button>
        </span>
      </slot>
    </div>
    <!-- @vue-expect-error function from inherited base component -->
    <div ref="editorElement" :class="cx('content')" :style="editorStyle"
      v-bind="ptm('content')"
      @focus="focused = true" @blur="focused = false"></div>
  </div>
</template>

<script lang="tsx">
// This (and related files) is a vendored version of the primevue quill plugin,
// vendoring Editor, BaseEditor, and EditorStyle with some changes.
// https://github.com/primefaces/primevue/issues/7041
import { shallowRef, type PropType } from "vue";
import { isExist } from "@primeuix/utils/dom";
import BaseEditor from "@/components/BaseEditor.vue";

import { default as Quill, type QuillOptions } from "quill";

// Parchment doesn't easily expose this type, so tear it off from another type
type RegistryType = NonNullable<QuillOptions["registry"]>;

export default {
  name: "Editor",
  extends: BaseEditor,
  inheritAttrs: false,
  emits: ["text-change", "selection-change", "load"],
  props: {
    theme: String,
    debug: [String, Boolean] as PropType<boolean | "error" | "warn" | "log" | "info">,
    registry: Object as PropType<RegistryType>,
    readOnly: Boolean,
    placeholder: String,
    bounds: [Object, String, null] as PropType<string | HTMLElement | null>,
    modules: Object,
    formats: [Array, null] as PropType<string[] | null>,
  },
  data() { return {
    quill: shallowRef<Quill | null>(null),
    focused: false,
  }},
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue && this.quill && !this.quill.hasFocus()) {
        this.renderValue(newValue);
      }
    },
    d_value(newValue, oldValue) {
      if (newValue !== oldValue && this.quill && !this.quill.hasFocus()) {
        this.renderValue(newValue);
      }
    },
    readonly() {
      if (this.quill != null) {
        this.quill.enable(!this.readonly);
      }
    },
    // quill isn't reconfigurable, have to recreate it every time
    // https://github.com/slab/quill/discussions/4397
    theme() { this.remountQuill(); },
    debug() { this.remountQuill(); },
    registry() { this.remountQuill(); },
    placeholder() { this.remountQuill(); },
    bounds() { this.remountQuill(); },
    modules() { this.remountQuill(); },
    formats() { this.remountQuill(); },
  },
  mounted() {
    this.remountQuill();
  },
  beforeUnmount() {
    this.quill = null;
  },
  methods: {
    async remountQuill() {
      this.quill = null;

      const quill = await import("quill");
      if (quill && isExist(this.$refs.editorElement as HTMLDivElement)) {
        const configuration = {
          modules: {
            toolbar: this.$refs.toolbarElement,
            ...this.modules
          },
          debug: this.debug,
          registry: this.registry,
          readOnly: this.readonly,
          theme: this.theme ?? "snow",
          formats: this.formats,
          bounds: this.bounds ?? (this.$refs.editorElement as HTMLDivElement),
          placeholder: this.placeholder
        };

        this.quill = new quill.default(
          this.$refs.editorElement as HTMLDivElement, configuration
        );
        this.initQuill();
      }
      this.handleLoad();
    },
    renderValue(value: string) {
      if (this.quill) {
        if (value) {
          const delta = this.quill.clipboard.convert({ html: value });
          this.quill.setContents(delta);
        } else {
          this.quill.setText("");
        }
      }
    },
    initQuill() {
      // @ts-expect-error d_value is the related value from baseeditableholder
      this.renderValue(this.d_value);
      this.focused = this.quill!.hasFocus();

      this.quill!.on("text-change", (delta, oldContents, source) => {
        this.focused = this.quill!.hasFocus();

        if (source === "user") {
          let html = this.quill!.getSemanticHTML();
          let text = this.quill!.getText().trim();

          if (html === "<p><br></p>" || html === "<p></p>") {
            html = "";
            text = "";
          }

          // @ts-expect-error from parent component
          this.writeValue(html);
          this.$emit("text-change", {
            htmlValue: html,
            textValue: text,
            delta: delta,
            source: source,
            instance: this.quill
          });
        }
      });

      this.quill!.on("selection-change", (range, oldRange, source) => {
        this.focused = this.quill!.hasFocus();

        let html = this.quill!.getSemanticHTML();
        let text = this.quill!.getText().trim();

        if (html === "<p><br></p>" || html === "<p></p>") {
          html = "";
          text = "";
        }

        this.$emit("selection-change", {
          htmlValue: html,
          textValue: text,
          range: range,
          oldRange: oldRange,
          source: source,
          instance: this.quill
        });
      });
    },
    handleLoad() {
      if (this.quill && this.quill.getModule("toolbar")) {
        this.$emit("load", { instance: this.quill });
      }
    }
  }
};
</script>

<style lang="less">
  /* copied from textarea floatlabel style */
  .p-floatlabel:has(
    .p-editor:not(.p-inputwrapper-focus):not(.p-inputwrapper-filled)
  ) label {
    /* quill snow theme:
    toolbar height + padding + container block start padding */
    --p-floatlabel-position-y: calc(24px + 8px * 2 + 12px + 0.25em);
    /* container inline start padding */
    --p-floatlabel-position-x: 15px;
    top: var(--p-floatlabel-position-y);
    transform: translateY(0);
  }

  .p-editor-toolbar * {
    flex: 0 0 auto;
  }

  // quill produces <ol> for a bulleted list
  // https://github.com/slab/quill/issues/3014
  .ql-editor ol li::before {
    content: "■" !important;
  }

  .ql-editor ol li[data-list="ordered"]::before {
    font-weight: bold;
    content: counter(list-0,decimal) '. ' !important;
  }

  .prose() {
    p {
      margin-block: var(--spacing-block);
      hyphens: auto;
    }

    ul, ol, dl {
      margin-block: var(--spacing-block);
      list-style-position: inside;
      ::marker { font-weight: 700; }
    }

    ul ul, ol ol { padding-inline-start: var(--spacing-block); }

    h1, h2, h3, h4 {
      margin-block: var(--spacing-block-heading) 0;

      & + * { margin-block-start: 0; }
    }

    h1 { margin-block-start: 0; }

    h5, h6 {
      display: inline;
      float: inline-start;
      text-transform: uppercase;
      margin-inline-end: var(--spacing-block);
    }

    b, strong { font-weight: bold; }
    i, em, cite { font-style: italic; }

    :first-child { margin-block-start: 0; }
    :last-child { margin-block-end: 0; }
  }

  .p-editor-content {
    font: inherit !important;
  }

  .p-editor-content .ql-editor {
    .prose() !important;
  }
</style>
